import type { TFunction } from 'i18next';
import type { Invoice } from 'korint-sdk';

export type InvoiceDocument = {
  id: string;
  signedUrl: string;
  fileName: string;
  /** Set only when the invoice has one quittance per insurer */
  label?: string;
  insurer?: string;
};

export type InvoiceInsurerLine = {
  insurer: string;
  label: string;
  amount: number;
  document?: InvoiceDocument;
};

const insurerName = (insurer: string) => insurer.charAt(0) + insurer.slice(1).toLowerCase();

const insurerAmount = (invoice: Invoice, insurer: string) =>
  invoice.lines
    .filter(line => line.insurer === insurer)
    .reduce((sum, line) => sum + line.total.amountWithFeesAndTaxes, 0);

const quittanceLabel = (invoice: Invoice, insurer: string, t: TFunction) =>
  t(
    insurerAmount(invoice, insurer) < 0
      ? 'invoices.module.insurerCreditQuittance'
      : 'invoices.module.insurerQuittance',
    { insurer: insurerName(insurer) },
  );

export const invoiceDocumentsOf = (invoice: Invoice, t: TFunction): InvoiceDocument[] => {
  const documents =
    invoice.documents ?? (invoice.signedUrl ? [{ signedUrl: invoice.signedUrl }] : []);
  const fileName = `Facture-${invoice.externalId || invoice.id}.pdf`;

  return documents.map(({ signedUrl, insurer }, index) => {
    if (documents.length < 2 || !insurer) {
      return { id: index === 0 ? invoice.id : `${invoice.id}-${index}`, signedUrl, fileName };
    }

    const label = quittanceLabel(invoice, insurer, t);

    return {
      id: `${invoice.id}-${insurer}`,
      signedUrl,
      fileName: `${label} - ${fileName}`,
      label,
      insurer,
    };
  });
};

// One line per insurer billed on the invoice; empty when it bills a single one.
export const invoiceInsurerLinesOf = (invoice: Invoice, t: TFunction): InvoiceInsurerLine[] => {
  const insurers = [...new Set(invoice.lines.flatMap(({ insurer }) => (insurer ? [insurer] : [])))];
  if (insurers.length < 2) {
    return [];
  }

  const documents = invoiceDocumentsOf(invoice, t);

  return insurers.map(insurer => ({
    insurer,
    label: quittanceLabel(invoice, insurer, t),
    amount: insurerAmount(invoice, insurer),
    document: documents.find(document => document.insurer === insurer),
  }));
};
